import React, { useEffect } from "react";
import { Button, Modal, Tabs } from "antd";
import { ShippingWay } from "@/services/api/orderShip";
import EwaybillResult from "./EwaybillResult";
import MultiPlatformEwaybillResultStore from "./MultiPlatformEwaybillResultStore";
import { useMemoizedFn } from "ahooks";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { observer } from "mobx-react-lite";
import AllEwaybillResultAlert from "./AllEwaybillResultAlert";
import { useGlobalStore } from "@/store/hooks";
import PrintFailOperation from "./PrintFailOperation";
import { OnShipTaskDone } from "@/scenes/OrderManagement/components/dialogs/ShipDialog/useShipFlow";
import useShip from "@/scenes/OrderManagement/components/dialogs/ShipDialog/useShip";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { validateShipping } from "@/services/ship";
import { ShipValidationRuleKey } from "@/services/ship/type";
import { BatchUpdateSellerRemarksDialogProps } from "@/scenes/OrderManagement/components/dialogs/BatchUpdateSellerRemarksDialog";
import { TabItem, buildTabsItems } from "@/utils/antdItems";

interface PrintEwaybillResultModalProps
    extends InjectBaseModalProps,
        Pick<BatchUpdateSellerRemarksDialogProps, "onUpdateOrderSellerRemarks" | "onUpdateSupplierOrderRemarks"> {
    store: MultiPlatformEwaybillResultStore;
    onTaskDone: OnShipTaskDone;
}

const PrintEwaybillResultModal: React.FC<PrintEwaybillResultModalProps> = function (props) {
    const { open, onCancel, store, onTaskDone, onUpdateOrderSellerRemarks, onUpdateSupplierOrderRemarks } = props;
    const globalStore = useGlobalStore();
    const { submitShip } = useShip(onTaskDone);

    // 发货操作
    const handleShip = useMemoizedFn(async () => {
        const selectedOrders = store.getSelectedOrders();

        if (selectedOrders.length === 0) {
            return Modal.error({
                title: "请先至少选择一个订单",
            });
        }

        const { canShipOrders, canShip } = await validateShipping({
            globalStore,
            orders: selectedOrders,
            rule: {
                [ShipValidationRuleKey.checkDouDianRefundingOrder]: true,
            },
        });

        if (!canShip || canShipOrders.length === 0) return;

        submitShip({
            orders: canShipOrders,
            shippingWay: ShippingWay.OFFLINE,
            ewaybillMapping: store.ewaybillMapping,
        });

        onCancel();
    });

    useEffect(() => {
        store.selectAllOrder();
    }, []);

    const hasAliOutOrder = !!(
        store.ali_out_order_dy ||
        store.ali_out_order_kwai ||
        store.ali_out_order_pdd ||
        store.ali_out_order_taobao ||
        store.ali_out_order_xhs ||
        store.ali_out_order_jd
    );

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            width={920}
            title="电子面单获取和发送到打印机的结果"
            mask={{ closable: false }}
            styles={{ body: {
                minHeight: 500,
            } }}
            footer={
                <div style={{ display: "flex" }}>
                    <div style={{ flex: "1", textAlign: "left" }}>
                        <PrintFailOperation
                            store={store}
                            onUpdateOrderSellerRemarks={onUpdateOrderSellerRemarks}
                            onUpdateSupplierOrderRemarks={onUpdateSupplierOrderRemarks}
                        />
                    </div>

                    {store.showShip ? (
                        <AutoLoadingButton type="primary" className="ant-btn-warning mr10" onClick={handleShip}>
                            马上发货
                        </AutoLoadingButton>
                    ) : null}

                    <Button onClick={onCancel}>{store.showShip ? "等会再发货" : "关闭"}</Button>
                </div>
            }
        >
            <p className="mb10 text-gray">
                温馨提示：当前窗口发货仅用当前打印单号，旧单号不回传。如平台支持多包裹且想旧单号一起回传，请到订单列表发货
            </p>
            {hasAliOutOrder ? (
                <p className="mb10 text-gray">
                    温馨提示：平台分销订单打印时部分平台电子面单会自动根据订单号打印该分销订单的最新地址信息，可能会出现打印地址不一致，建议联系买家确认，
                    <a href="https://qw15p3s8hv.feishu.cn/wiki/VcxCwTJtgi0sc2kkaqicbsm4nec" target="_blank">
                        查看详细说明
                    </a>
                </p>
            ) : null}

            <AllEwaybillResultAlert store={store} />

            {store.isGenerateWaybillNoOnly ? (
                <EwaybillResult store={store} printerName={""} />
            ) : (
                <Tabs items={buildTabsItems(<>
                    {store.printerNames.map((printerName) => {
                        const ewaybillResultStores = store.platformStoresMapping.get(printerName);

                        let errorCount = 0;

                        for (let index = 0; index < ewaybillResultStores?.length; index++) {
                            const ewaybillResultStore = ewaybillResultStores[index];

                            errorCount += ewaybillResultStore.generateErrorCount + ewaybillResultStore.printErrorCount;
                        }

                        return (
                            <TabItem
                                key={printerName}
                                tab={
                                    <>
                                        <span>{printerName}</span>

                                        {errorCount ? <span className="text-error">(失败{errorCount}个)</span> : null}
                                    </>
                                }
                            >
                                <EwaybillResult store={store} printerName={printerName} />
                            </TabItem>
                        );
                    })}
                </>)} />
            )}
        </Modal>
    );
};

export default observer(PrintEwaybillResultModal);
